import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronRight, Shield, Cpu, Radio, CheckCircle } from "lucide-react";

const stats = [
  { icon: Shield, label: "FAA / EASA", sub: "Compatible" },
  { icon: Cpu, label: "Failure Injection", sub: "All Systems" },
  { icon: Radio, label: "G1000NXi", sub: "Add-On" },
];

const badges = ["Prepar3D v4/v5/v6", "Windows 10/11 x64", "Multi-Display", "Custom Branding"];

export function HeroSection() {
  const go = (href: string) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative pt-16 overflow-hidden">
      {/* Navy top strip */}
      <div className="bg-primary text-primary-foreground py-2 text-center text-xs font-mono tracking-widest uppercase">
        Professional Aviation Training Software — ATO Licensed
      </div>

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-20 lg:py-28">
          {/* LEFT */}
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05, type: "spring", stiffness: 280, damping: 32 }}
              className="inline-flex items-center gap-2 bg-secondary border border-border rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase font-mono">G1000NXi Add-On for Prepar3D</span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12, type: "spring", stiffness: 280, damping: 32 }}
              className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] mb-6 text-foreground"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Welcome to<br />
              <span style={{ color: "oklch(0.28 0.09 240)" }}>EfisFly</span>{" "}
              <span style={{ color: "oklch(0.55 0.22 25)" }}>G1000</span><br />
              <span className="text-4xl md:text-5xl text-muted-foreground font-bold">Professional</span>
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 280, damping: 32 }}
              className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
              features G1000NXi Add-Ons, airports, CBTs for Lockheed Martin Prepar3D.<br />
              Professional Aviation Training Software designed for ATOs.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, type: "spring", stiffness: 280, damping: 32 }}
              className="flex flex-wrap gap-3 mb-10">
              {badges.map(b => (
                <span key={b} className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-secondary border border-border rounded-md px-3 py-1.5">
                  <CheckCircle className="w-3 h-3 text-primary" />{b}
                </span>
              ))}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, type: "spring", stiffness: 280, damping: 32 }}
              className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="bg-primary text-primary-foreground font-bold px-8 shadow-[0_4px_16px_oklch(0.28_0.09_240/0.35)] hover:shadow-[0_6px_24px_oklch(0.28_0.09_240/0.5)] transition-shadow"
                onClick={() => go("#pricing")}>
                Get Professional License <ChevronRight className="ml-1 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-border font-semibold text-foreground hover:bg-secondary"
                onClick={() => go("#products")}>
                More Info
              </Button>
            </motion.div>
          </div>

          {/* RIGHT — instrument panel visual */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 220, damping: 30 }}
            className="relative hidden lg:block">
            {/* Main panel frame */}
            <div className="relative rounded-2xl border-2 border-primary/20 bg-primary p-1 shadow-[0_24px_80px_oklch(0.28_0.09_240/0.25)]">
              <div className="rounded-xl bg-[oklch(0.08_0.03_240)] p-6 min-h-[380px] flex flex-col gap-4">
                {/* Fake PFD top bar */}
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-xs font-mono text-green-400 tracking-widest">PFD</span>
                  <div className="flex gap-2">
                    {["NAV", "GPS", "VLOC"].map(m => (
                      <span key={m} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-white/40">{m}</span>
                    ))}
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-green-400/20 text-green-400">GPS</span>
                  </div>
                  <span className="text-xs font-mono text-white/30 tracking-widest">MFD</span>
                </div>
                {/* Fake attitude indicator */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="relative w-48 h-48">
                    <div className="absolute inset-0 rounded-full border-2 border-white/10 overflow-hidden">
                      <div className="absolute inset-0 bg-[oklch(0.35_0.12_240)]" style={{ clipPath: "inset(0 0 50% 0)" }} />
                      <div className="absolute inset-0 bg-[oklch(0.55_0.15_40)]" style={{ clipPath: "inset(50% 0 0 0)" }} />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-32 h-0.5 bg-white/80" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 border-2 border-white rounded-full" />
                    </div>
                    {/* Speed tape */}
                    <div className="absolute left-0 top-6 bottom-6 w-8 bg-black/50 border border-white/10 rounded flex flex-col items-center justify-center gap-1">
                      {[140, 135, 130, 125, 120].map(n => (
                        <span key={n} className="text-[8px] font-mono text-white/60">{n}</span>
                      ))}
                    </div>
                    {/* Alt tape */}
                    <div className="absolute right-0 top-6 bottom-6 w-8 bg-black/50 border border-white/10 rounded flex flex-col items-center justify-center gap-1">
                      {[2500, 2400, 2300, 2200, 2100].map(n => (
                        <span key={n} className="text-[8px] font-mono text-white/60">{n}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {/* HSI strip */}
                <div className="border-t border-white/10 pt-3 flex items-center justify-center gap-6">
                  {["ILS", "360°", "GPS", "ALT"].map(v => (
                    <span key={v} className="text-[10px] font-mono text-white/40">{v}</span>
                  ))}
                </div>
              </div>
            </div>
            {/* Decorative tags */}
            <div className="absolute -right-4 top-8 bg-white border border-border rounded-lg shadow-lg px-3 py-2 text-xs font-mono">
              <span className="text-muted-foreground">SYN VIS</span> <span className="text-primary font-bold">ON</span>
            </div>
            <div className="absolute -left-4 bottom-12 bg-white border border-border rounded-lg shadow-lg px-3 py-2 text-xs font-mono">
              <span className="text-muted-foreground">AFCS</span> <span style={{ color: "oklch(0.55 0.22 25)" }} className="font-bold">ACTIVE</span>
            </div>
          </motion.div>
        </div>

        {/* Stats row */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, type: "spring", stiffness: 280, damping: 32 }}
          className="border-t border-border py-8 grid grid-cols-3 gap-6">
          {stats.map(({ icon: Icon, label, sub }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="font-bold text-sm text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{label}</div>
                <div className="text-xs text-muted-foreground">{sub}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
